import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { every, find, isEqual, omit, some } from 'lodash-es'
import { LoadingButton } from '@mui/lab'
import { Alert, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import CrossSectionInput from '@editor/components/CrossSectionInput'
import { materialCategoryFromCrossSection } from '@editor/components/CrossSectionInput/utils'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { getDefaultCheckSettingsForElement } from '@queries'
import { resolveAssemblyStandardCrossSectionFromModel } from 'src/state/utils/deriveElementAssignments'
import { DefaultValues } from '../..'

interface Props {
  disabled: boolean
  elementType?: ElementTypes
  showPolicyToggle?: boolean
}

const FormFields = ({ disabled, elementType, showPolicyToggle = false }: Props) => {
  const { projectId } = useParams()
  const { getValues, setValue } = useFormContext<DefaultValues>()
  const model = useModelStore(state => state.model)

  const { enqueueSnackbar } = useSnackbar()

  const crossSection = useWatch<DefaultValues>({ name: 'crossSection' })
  const crossSectionPolicy = useWatch<DefaultValues>({ name: 'crossSectionPolicy' })
  const elements = useWatch<DefaultValues>({ name: 'elements' }) as DefaultValues['elements']
  const isAssemblyStandard = crossSectionPolicy === 'AssemblyStandard'

  const crossSectionsDiffer = useMemo(() => {
    if (!elements?.length) return false
    const referenceCS = omit(elements[0].crossSection, 'element_guid')
    return !every(elements, element =>
      isEqual(omit(element.crossSection, 'element_guid'), referenceCS),
    )
  }, [elements])

  const { mutateAsync: handleMaterialCategoryChange } = useMutation(
    async (nextCs: CrossSectionTimberOrCLTOrSteel) => {
      const formElements = getValues('elements')

      const newData: {
        elementGuid: string
        newSettings: SettingsOnMember
        newCrossSection: CrossSection
        crossedTimberSteel: boolean
      }[] = []

      for (const element of formElements) {
        const prevCs = element.crossSection

        const nextKind = 'material' in nextCs ? nextCs.material.kind : undefined

        if (nextKind && materialCategoryFromCrossSection(prevCs) === nextKind) continue

        const crossedTimberSteel = prevCs.kind !== nextCs.kind

        if (crossedTimberSteel && projectId && element.guid) {
          const data = await getDefaultCheckSettingsForElement.request(
            projectId,
            element.guid,
            nextCs,
          )
          newData.push({
            elementGuid: element.guid,
            newSettings: data.new_settings,
            newCrossSection: data.new_cs,
            crossedTimberSteel,
          })
        }
      }

      return {
        newData,
        elements: formElements,
      }
    },
    {
      onSuccess: ({ newData, elements: formElements }) => {
        const newElementsData: DefaultValues['elements'] = formElements.map(element => {
          const elementData = find(newData, { elementGuid: element.guid })
          return {
            ...element,
            checkSettings: elementData?.newSettings || element.checkSettings,
            crossSection: elementData?.newCrossSection || element.crossSection,
          }
        })

        setValue('elements', newElementsData, { shouldDirty: true, shouldTouch: true })

        const atLeastOneCrossedTimberSteel = some(newData, { crossedTimberSteel: true })
        const anyHasResults = some(formElements, element => element.checks.length > 0)
        if (atLeastOneCrossedTimberSteel && anyHasResults) {
          enqueueSnackbar(
            'Der Materialtyp wurde bei mindestens einem Element verändert, bitte Nachweiseinstellungen genau prüfen',
            {
              variant: 'info',
            },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          'Querschnitt konnte nicht gewechselt werden. Nachweiseinstellungen unverändert',
          {
            variant: 'error',
          },
        )
      },
    },
  )

  const { mutateAsync: onPressChange, isLoading: isChangingCrossSection } = useMutation({
    mutationKey: [projectId, 'changeCrossSectionMultiEdit'],
    mutationFn: async () => {
      const formElements = getValues('elements')
      const policy = getValues('crossSectionPolicy')
      const firstElement = formElements[0]
      if (!firstElement) return

      if (policy === 'AssemblyStandard') {
        const sharedCs =
          resolveAssemblyStandardCrossSectionFromModel(model, firstElement.guid) ??
          firstElement.crossSection

        // Apply material/settings updates first, then force assembly CS onto each element.
        await handleMaterialCategoryChange(sharedCs as CrossSectionTimberOrCLTOrSteel)
        const afterMaterial = getValues('elements')
        const assemblyElements = afterMaterial.map(element => ({
          ...element,
          crossSection:
            (resolveAssemblyStandardCrossSectionFromModel(model, element.guid) as
              | CrossSection
              | undefined) ?? element.crossSection,
        }))
        setValue('elements', assemblyElements, { shouldDirty: true, shouldTouch: true })
        setValue('crossSection', sharedCs, { shouldDirty: true, shouldTouch: true })
        return
      }

      const sharedCs = firstElement.crossSection
      await handleMaterialCategoryChange(sharedCs as CrossSectionTimberOrCLTOrSteel)
      setValue('crossSection', sharedCs, { shouldDirty: true, shouldTouch: true })
    },
  })

  const policyToggle = showPolicyToggle ? (
    <>
      <Typography variant="body2">Querschnittsquelle</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        value={crossSectionPolicy ?? null}
        onChange={(_, value: CrossSectionPolicy | null) => {
          if (!value || value === crossSectionPolicy) return
          setValue('crossSectionPolicy', value, { shouldDirty: true, shouldTouch: true })

          // Before "Ändern" only the selected tab changes — CS/scene stay untouched.
          if (!crossSection) return

          if (value === 'AssemblyStandard') {
            const nextElements = getValues('elements').map(element => ({
              ...element,
              crossSection:
                (resolveAssemblyStandardCrossSectionFromModel(model, element.guid) as
                  | CrossSection
                  | undefined) ?? element.crossSection,
            }))
            setValue('elements', nextElements, { shouldDirty: true, shouldTouch: true })
          }
        }}
        data-cy="cross-section-policy-toggle"
      >
        <ToggleButton value="AssemblyStandard" data-cy="cs-policy-assembly-standard">
          Aus Aufbau
        </ToggleButton>
        <ToggleButton value="UserDefined" data-cy="cs-policy-user-defined">
          Benutzerdefiniert
        </ToggleButton>
      </ToggleButtonGroup>
    </>
  ) : null

  // Editing still gated behind "Ändern" (differing cross sections): the policy
  // toggle is not shown here — switching it would not change anything until
  // "Ändern" unlocks, so it only appears together with the input fields.
  if (!crossSection) {
    return (
      <Stack direction="column" spacing={1}>
        <LoadingButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => onPressChange()}
          loading={isChangingCrossSection}
          data-cy="crosssection-change-button"
        >
          Ändern
        </LoadingButton>
        {crossSectionsDiffer && (
          <Alert severity="warning">Elemente haben unterschiedliche Querschnitte</Alert>
        )}
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={1}>
      {policyToggle}

      <CrossSectionInput
        crossSectionFieldPath="crossSection"
        disabled={disabled || isAssemblyStandard}
        elementType={elementType}
        onChangeMaterialCategory={async (_currentCs, nextCs) =>
          handleMaterialCategoryChange(nextCs as CrossSectionTimberOrCLTOrSteel)
        }
      />

      {crossSectionsDiffer && !isAssemblyStandard && (
        <Alert severity="warning">Elemente haben unterschiedliche Querschnitte</Alert>
      )}
    </Stack>
  )
}

export default FormFields
